import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ProjectApi } from "../api/projectApi";
import { useProjectStore } from "../core_stores/useProjectStore";

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

interface Param {
  original: string;
  current: string;
}

interface SettingsActions {
  initialize: () => Promise<void>;
  appNameChanged: (appName: string) => void;
  packageNameChanged: (packageName: string) => void;
  appVersionChanged: (appVersion: string) => void;
  submit: () => Promise<void>;
  undo: () => void;
  dispose: () => void;
}

interface SettingsState {
  appName: Param | null;
  packageName: Param | null;
  appVersion: Param | null;

  // Validation errors
  appNameError: string | null;
  packageNameError: string | null;
  appVersionError: string | null;

  canSubmit: boolean;

  actions: SettingsActions;
}

const useProjectSettingsStore = create<SettingsState>()((set, get) => {
  let unsubscribeLatestMsg: (() => void) | null = null;
  let unsubscribeHistory: (() => void) | null = null;

  async function loadSettings() {
    const projectId = useProjectStore.getState().projectId!;
    if (!projectId) {
      return;
    }
    const data = await ProjectApi.loadProjectSettings(projectId);
    set({
      appName: {
        original: data.app_name,
        current: data.app_name,
      },
      packageName: {
        original: data.package_name,
        current: data.package_name,
      },
      appVersion: {
        original: data.app_version,
        current: data.app_version,
      },
      canSubmit: false,
    });
  }

  function validateAppName(appName: string): ValidationResult {
    if (!appName || appName.trim().length === 0) {
      return { isValid: false, error: `App name cannot be empty` };
    }

    if (appName.trim().length < 1) {
      return {
        isValid: false,
        error: `App name must be at least 1 character long`,
      };
    }

    if (appName.length > 30) {
      return {
        isValid: false,
        error: `App name cannot exceed 30 characters`,
      };
    }

    if (appName.trim().length === 0) {
      return {
        isValid: false,
        error: `App name cannot be spaces only`,
      };
    }

    return { isValid: true };
  }

  function validatePackageName(packageName: string): ValidationResult {
    if (!packageName || packageName.trim().length === 0) {
      return { isValid: false, error: "Package name cannot be empty" };
    }

    // Must start with a letter
    if (!/^[a-zA-Z]/.test(packageName)) {
      return {
        isValid: false,
        error: "Package name must start with a letter",
      };
    }

    // Must contain only letters, numbers, underscores, and dots
    if (!/^[a-zA-Z0-9_.]+$/.test(packageName)) {
      return {
        isValid: false,
        error:
          "Package name can only contain letters, numbers, underscores (_), and dots (.)",
      };
    }

    // Must be composed of at least 2 parts separated by dots
    const parts = packageName.split(".");
    if (parts.length < 2) {
      return {
        isValid: false,
        error:
          "Package name must contain at least 2 parts separated by dots (e.g., com.example)",
      };
    }

    // Each part must not be empty
    for (const part of parts) {
      if (part.length === 0) {
        return {
          isValid: false,
          error: "Package name parts cannot be empty",
        };
      }
    }

    return { isValid: true };
  }

  function validateAppVersion(appVersion: string): ValidationResult {
    if (!appVersion || appVersion.trim().length === 0) {
      return { isValid: false, error: "App version cannot be empty" };
    }

    // Split by dots to check each part
    const parts = appVersion.split(".");

    // Check if version has more than 3 parts
    if (parts.length > 3) {
      return {
        isValid: false,
        error: "App version can have at most 3 parts (e.g., 1.2.3)",
      };
    }

    for (const part of parts) {
      if (part.length === 0) {
        return { isValid: false, error: "Version parts cannot be empty" };
      }

      // Check if part is a valid non-negative number
      const num = parseInt(part, 10);
      if (isNaN(num) || num < 0) {
        return {
          isValid: false,
          error: "Version parts must be non-negative numbers",
        };
      }

      // Check if the string representation matches the number (to avoid leading zeros)
      if (num.toString() !== part) {
        return {
          isValid: false,
          error: "Version parts cannot have leading zeros",
        };
      }
    }

    return { isValid: true };
  }

  function updateCanSubmit() {
    set((state) => ({
      canSubmit:
        state.appNameError === null &&
        state.packageNameError === null &&
        state.appVersionError === null &&
        (state.appName?.current !== state.appName?.original ||
          state.packageName?.current !== state.packageName?.original ||
          state.appVersion?.current !== state.appVersion?.original),
    }));
  }

  return {
    appName: null,
    packageName: null,
    appVersion: null,

    // Validation errors
    appNameError: null,
    packageNameError: null,
    appVersionError: null,

    canSubmit: false,

    actions: {
      initialize: async () => {
        await loadSettings();
        unsubscribeLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (latestMsg) => {
            if (
              latestMsg &&
              latestMsg.type ===
                SharedMessageTypes.MessageTypeEnum.Enum.projectConfig
            ) {
              loadSettings();
            }
          }
        );
        unsubscribeHistory = useProjectStore.subscribe(
          (state) => state.history,
          (history) => {
            const latestProjectConfig = [...(history ?? [])]
              .reverse()
              .find(
                (msg) =>
                  msg.type ===
                  SharedMessageTypes.MessageTypeEnum.Enum.projectConfig
              );
            if (latestProjectConfig) {
              loadSettings();
            }
          }
        );
      },

      appNameChanged: (appName: string) => {
        const validation = validateAppName(appName);
        set((state) => ({
          appName: {
            original: state.appName!.original,
            current: appName,
          },
          appNameError: validation.isValid ? null : validation.error || null,
        }));
        updateCanSubmit();
      },

      packageNameChanged: (packageName: string) => {
        const validation = validatePackageName(packageName);
        set((state) => ({
          packageName: {
            original: state.packageName!.original,
            current: packageName,
          },
          packageNameError: validation.isValid
            ? null
            : validation.error || null,
        }));
        updateCanSubmit();
      },

      appVersionChanged: (appVersion: string) => {
        const validation = validateAppVersion(appVersion);
        set((state) => ({
          appVersion: {
            original: state.appVersion!.original,
            current: appVersion,
          },
          appVersionError: validation.isValid ? null : validation.error || null,
        }));
        updateCanSubmit();
      },

      submit: async () => {
        const state = get();
        const projectId = useProjectStore.getState().projectId;

        if (projectId) {
          await ProjectApi.updateProjectSettings({
            projectId,
            appName: state.appName!.current,
            packageName: state.packageName!.current,
            appVersion: state.appVersion!.current,
          });
          get().actions.initialize();
        }
      },

      undo: () => {
        set((state) => ({
          appName: {
            original: state.appName!.original,
            current: state.appName!.original,
          },
          packageName: {
            original: state.packageName!.original,
            current: state.packageName!.original,
          },
          appVersion: {
            original: state.appVersion!.original,
            current: state.appVersion!.original,
          },
          appNameError: null,
          packageNameError: null,
          appVersionError: null,
        }));
      },

      dispose: () => {
        if (unsubscribeLatestMsg) {
          unsubscribeLatestMsg();
          unsubscribeLatestMsg = null;
        }
        if (unsubscribeHistory) {
          unsubscribeHistory();
          unsubscribeHistory = null;
        }
        set({
          appName: null,
          packageName: null,
          appVersion: null,
          appNameError: null,
          packageNameError: null,
          appVersionError: null,
        });
      },
    },
  };
});

export const useProjectSettingsAppName = () =>
  useProjectSettingsStore((state) => state.appName);

export const useProjectSettingsPackageName = () =>
  useProjectSettingsStore((state) => state.packageName);

export const useProjectSettingsAppVersion = () =>
  useProjectSettingsStore((state) => state.appVersion);

export const useProjectSettingsAppNameError = () =>
  useProjectSettingsStore((state) => state.appNameError);

export const useProjectSettingsPackageNameError = () =>
  useProjectSettingsStore((state) => state.packageNameError);

export const useProjectSettingsAppVersionError = () =>
  useProjectSettingsStore((state) => state.appVersionError);

export const useProjectSettingsCanSubmit = () =>
  useProjectSettingsStore((state) => state.canSubmit);

export const useProjectSettingsActions = () =>
  useProjectSettingsStore((state) => state.actions);
